import React, { useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import useTranslation from 'next-translate/useTranslation';
import { LoginLayout } from '@components/Layouts/LoginLayout';
import { ILoginRequest, useAuthenticateUserMutation } from '@/stores/userApi';
import { ActivityIndicator } from '@components/ActivityIndicator/ActivityIndicator';
import styles from './login.module.scss';

const getSafeRedirectUrl = (value: string | null): string | null => {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return null;
  }

  try {
    const url = new URL(value, window.location.origin);

    return url.origin === window.location.origin ? url.pathname + url.search + url.hash : null;
  } catch {
    return null;
  }
};

const Login = () => {
  const [authenticateUser] = useAuthenticateUserMutation();

  const { t } = useTranslation('login');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [noUsernameError, setNoUsernameError] = useState('');
  const [noPasswordError, setNoPasswordError] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();
    if (!username) {
      setNoUsernameError(t('error.empty-field'));
      return;
    }
    setNoUsernameError('');

    if (!password) {
      setNoPasswordError(t('error.empty-field'));
      return;
    }
    setNoPasswordError('');

    const values: ILoginRequest = { username, password };
    try {
      setError('');
      setIsLoading(true);
      const loginResponse = await authenticateUser(values).unwrap();
      const returnUrl = new URLSearchParams(window.location.search).get('returnUrl');
      const redirectUrl = getSafeRedirectUrl(returnUrl) ?? loginResponse.redirectUrl;
      window.location.replace(redirectUrl);
    } catch (error) {
      setError(t('error.incorrect-username-or-password'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <ActivityIndicator />}

      <Container className={`${styles.formContainer} d-flex align-items-center justify-content-center`}>
        <Row>
          <Col>
            <h1 className={styles.title}>{t('Mijn omgeving')}</h1>
            <Form className={styles.form}>
              <Form.Group controlId="formUsername">
                <Form.Label>{t('label.username')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('label.username')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                {noUsernameError && <p className="text-danger">{noUsernameError}</p>}
              </Form.Group>

              <Form.Group controlId="formPassword">
                <Row>
                  <Col>
                    <Form.Label>{t('label.password')}</Form.Label>
                  </Col>
                  <Col className={styles.forgotPassword}>
                    <Form.Label tabIndex={-1} as="a" href="/wachtwoord-vergeten">
                      {t('Wachtwoord vergeten')}
                    </Form.Label>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Control
                      type="password"
                      placeholder={t('label.password')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    {error && <p className="text-danger">{error}</p>}
                    {noPasswordError && <p className="text-danger">{noPasswordError}</p>}
                  </Col>
                </Row>
              </Form.Group>

              <Button type="submit" block size={'xl' as any} variant="primary" onClick={(e) => handleLogin(e)}>
                {t('button.login')}
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </>
  );
};

Login.getLayout = function getLayout(page: React.ReactNode) {
  return <LoginLayout>{page}</LoginLayout>;
};

export default Login;
